import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../../utils/api";
import useCurrentUser from "../../hooks/useCurrentUser";
import {
  Plus, Trash2, CheckCircle, AlertCircle, X,
  Search, Package, ChevronDown, ChevronUp, Send, Upload, FileText,
} from "lucide-react";

const STATUS_CFG = {
  draft:       { cls: "bg-slate-100 text-slate-500",     label: "Draft" },
  submitted:   { cls: "bg-blue-100 text-blue-700",       label: "Submitted" },
  approved:    { cls: "bg-emerald-100 text-emerald-700", label: "Approved" },
  rejected:    { cls: "bg-red-100 text-red-600",         label: "Rejected" },
  in_transit:  { cls: "bg-amber-100 text-amber-700",     label: "In Transit" },
  arrived:     { cls: "bg-violet-100 text-violet-700",   label: "Arrived" },
  validating:  { cls: "bg-orange-100 text-orange-700",   label: "Validating" },
  validated:   { cls: "bg-teal-100 text-teal-700",       label: "Validated" },
  completed:   { cls: "bg-green-100 text-green-700",     label: "Completed" },
};

const EMPTY_ITEM = { sku: "", description: "", qty: 1, unit: "each", unitWeight: "", material: "" };

function Badge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.draft;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
}

const ACCEPTED_EDI = ".csv,.json,.xml,.edi,.txt";

function detectFormat(filename, content) {
  const ext = filename.split(".").pop().toLowerCase();
  if (ext === "csv") return "csv";
  if (ext === "json") return "json";
  if (ext === "xml") return "xml";
  if (["edi", "x12"].includes(ext) && content.trimStart().startsWith("ISA")) return "x12";
  if (["edi", "edifact"].includes(ext)) return "edifact";
  if (content.trimStart().startsWith("{") || content.trimStart().startsWith("[")) return "json";
  if (content.trimStart().startsWith("<")) return "xml";
  if (content.trimStart().startsWith("ISA")) return "x12";
  return "csv";
}

export default function VendorInbound() {
  const { clientId, userName } = useCurrentUser();
  const [shipments, setShipments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [modal, setModal] = useState(false);
  const [ediModal, setEdiModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ediUploading, setEdiUploading] = useState(false);
  const [ediFile, setEdiFile] = useState(null);
  const [ediPreview, setEdiPreview] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const ediFileRef = useRef(null);

  const [form, setForm] = useState({
    companyId: "", warehouseId: "", expectedArrival: "", notes: "",
    items: [{ ...EMPTY_ITEM }],
  });

  const flash = (msg, type = "success") => {
    if (type === "success") { setSuccess(msg); setTimeout(() => setSuccess(""), 4000); }
    else { setError(msg); setTimeout(() => setError(""), 5000); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [asnRes, compRes, whRes] = await Promise.all([
        api.get("/api/inbound"),
        api.get("/api/admin/companies").catch(() => ({ data: { companies: [] } })),
        api.get("/api/warehouses"),
      ]);
      setShipments(asnRes.data.shipments || []);
      setCompanies(compRes.data.companies || []);
      setWarehouses(whRes.data.warehouses || []);
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setItem = (idx, field, val) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: val };
      return { ...f, items };
    });
  };

  const handleCreate = async () => {
    if (!form.companyId) { flash("Select the receiving company", "error"); return; }
    if (form.items.some(i => !i.description.trim())) { flash("All items need a description", "error"); return; }
    setSaving(true);
    try {
      await api.post("/api/inbound", { ...form, vendorName: userName });
      flash("ASN created as draft.");
      setModal(false);
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (id) => {
    try {
      await api.patch(`/api/inbound/${id}/status`, { status: "submitted" });
      flash("ASN submitted to client for approval.");
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  const handleMarkTransit = async (id) => {
    try {
      await api.patch(`/api/inbound/${id}/status`, { status: "in_transit" });
      flash("Marked as In Transit.");
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this draft ASN?")) return;
    try {
      await api.delete(`/api/inbound/${id}`);
      flash("ASN deleted.");
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  const handleEdiFileSelect = (f) => {
    if (!f) return;
    if (f.size > 524288) { flash("File too large — max 512KB", "error"); return; }
    setEdiFile(f);
    const reader = new FileReader();
    reader.onload = e => setEdiPreview((e.target.result || "").slice(0, 300));
    reader.readAsText(f);
  };

  const handleEdiUpload = async () => {
    if (!ediFile) { flash("Select a file first", "error"); return; }
    setEdiUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const content = e.target.result || "";
        const format = detectFormat(ediFile.name, content);
        try {
          const upRes = await api.post("/api/edi/upload", {
            filename: ediFile.name,
            format,
            content,
            companyId: clientId,
          });
          const docId = upRes.data.documentId;
          // Auto-parse
          const parseRes = await api.post(`/api/edi/documents/${docId}/parse`);
          // Auto-process → create ASN draft
          const procRes = await api.post(`/api/edi/documents/${docId}/process`);
          flash(`ASN ${procRes.data.asnNumber} created as draft from ${ediFile.name} (${parseRes.data.itemCount} items). It awaits client approval.`);
          setEdiModal(false);
          setEdiFile(null);
          setEdiPreview("");
          load();
        } catch (err) {
          flash(err.response?.data?.error || "EDI upload failed", "error");
        } finally {
          setEdiUploading(false);
        }
      };
      reader.readAsText(ediFile);
    } catch {
      flash("Failed to read file", "error");
      setEdiUploading(false);
    }
  };

  const filtered = shipments.filter(s =>
    s.asnNumber?.toLowerCase().includes(search.toLowerCase()) ||
    s.companyId?.toLowerCase().includes(search.toLowerCase()) ||
    s.status?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-violet-100 rounded-xl"><Package size={24} className="text-violet-600"/></div>
          <div>
            <h1 className="text-2xl font-bold text-navy-900">Inbound Shipments (ASN)</h1>
            <p className="text-slate-500 text-sm">Create Advanced Shipping Notices for your clients</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setEdiFile(null); setEdiPreview(""); setEdiModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-bold hover:bg-violet-700 transition-colors">
            <Upload size={15}/> Upload ASN File
          </button>
          <button onClick={() => { setForm({ companyId: "", warehouseId: "", expectedArrival: "", notes: "", items: [{ ...EMPTY_ITEM }] }); setModal(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-navy-900 text-white rounded-xl text-sm font-bold hover:bg-navy-800 transition-colors">
            <Plus size={16}/> New ASN
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"><AlertCircle size={16}/>{error}</div>}
      {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2"><CheckCircle size={16}/>{success}</div>}

      {/* Stats */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-6">
        {["draft","submitted","approved","in_transit","completed"].map(s => (
          <div key={s} className="bg-white rounded-2xl border border-slate-200 p-4 text-center shadow-sm">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{STATUS_CFG[s]?.label}</p>
            <p className="text-xl font-bold text-navy-900">{shipments.filter(sh => sh.status === s).length}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex items-center gap-3">
        <Search size={16} className="text-slate-400"/>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by ASN number, company, or status..."
          className="flex-1 text-sm focus:outline-none text-slate-700 placeholder-slate-400"/>
        {search && <button onClick={() => setSearch("")}><X size={15} className="text-slate-400"/></button>}
      </div>

      {/* ASN List */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center text-slate-400 text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center text-slate-400 text-sm">
            No ASNs yet. Create your first one to start sending goods.
          </div>
        ) : filtered.map(s => (
          <div key={s.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <button onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="text-slate-400 hover:text-slate-600">
                  {expanded === s.id ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                </button>
                <div>
                  <p className="font-bold text-navy-900 font-mono">{s.asnNumber}</p>
                  <p className="text-xs text-slate-400">To: {s.companyId} {s.warehouseId ? `· ${s.warehouseId}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400">{s.items?.length || 0} items</span>
                {s.expectedArrival && <span className="text-xs text-slate-400">ETA {s.expectedArrival}</span>}
                <Badge status={s.status}/>
                {s.status === "draft" && (
                  <button onClick={() => handleSubmit(s.id)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700">
                    <Send size={12}/> Submit
                  </button>
                )}
                {s.status === "approved" && (
                  <button onClick={() => handleMarkTransit(s.id)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-lg hover:bg-amber-600">
                    Mark In Transit
                  </button>
                )}
                {s.status === "draft" && (
                  <button onClick={() => handleDelete(s.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            </div>
            {expanded === s.id && (
              <div className="border-t border-slate-100 px-6 py-4 bg-slate-50">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      <th className="text-left pb-2">SKU</th>
                      <th className="text-left pb-2">Description</th>
                      <th className="text-left pb-2">Material</th>
                      <th className="text-right pb-2">Qty</th>
                      <th className="text-right pb-2">Unit</th>
                      {s.status === "validated" || s.status === "completed" ? <th className="text-right pb-2">Actual Qty</th> : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(s.items || []).map((item, i) => (
                      <tr key={i}>
                        <td className="py-2 font-mono text-xs text-slate-500">{item.sku || "—"}</td>
                        <td className="py-2 text-slate-700">{item.description}</td>
                        <td className="py-2 text-slate-500 text-xs">{item.material || "—"}</td>
                        <td className="py-2 text-right text-slate-600">{item.qty}</td>
                        <td className="py-2 text-right text-slate-500 text-xs">{item.unit}</td>
                        {(s.status === "validated" || s.status === "completed") && (
                          <td className={`py-2 text-right font-semibold text-xs ${item.discrepancy !== 0 ? "text-red-500" : "text-emerald-600"}`}>
                            {item.actualQty ?? "—"} {item.discrepancy != null && item.discrepancy !== 0 ? `(${item.discrepancy > 0 ? "+" : ""}${item.discrepancy})` : ""}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {s.notes && <p className="mt-3 text-xs text-slate-500"><span className="font-semibold">Notes:</span> {s.notes}</p>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* EDI Upload Modal */}
      {ediModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-violet-600"/>
                <h2 className="font-bold text-navy-900 text-lg">Upload ASN File</h2>
              </div>
              <button onClick={() => setEdiModal(false)}><X size={18} className="text-slate-400"/></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-xs text-slate-500">
                Upload a CSV, JSON, XML, or EDI (X12 856 / EDIFACT DESADV) file. TinyGlobal will parse it and create a draft ASN for client approval. <strong>No inventory is updated until the 3PL validates the shipment.</strong>
              </p>

              <div
                onClick={() => ediFileRef.current?.click()}
                className="border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center cursor-pointer hover:border-violet-300 hover:bg-slate-50 transition-all">
                <input ref={ediFileRef} type="file" accept={ACCEPTED_EDI} className="hidden"
                  onChange={e => handleEdiFileSelect(e.target.files[0])}/>
                <Upload size={28} className="mx-auto text-slate-300 mb-2"/>
                <p className="text-sm font-semibold text-slate-500">
                  {ediFile ? ediFile.name : "Click to select file"}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">CSV, JSON, XML, .edi, .txt · max 512KB</p>
              </div>

              {ediPreview && (
                <pre className="text-[10px] bg-slate-50 border border-slate-100 rounded-xl p-3 max-h-20 overflow-auto text-slate-400 font-mono">
                  {ediPreview}{ediPreview.length >= 300 ? "…" : ""}
                </pre>
              )}
            </div>
            <div className="px-6 pb-6 pt-4 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setEdiModal(false)} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleEdiUpload} disabled={!ediFile || ediUploading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-50">
                <Upload size={14}/> {ediUploading ? "Uploading & Parsing…" : "Upload & Create ASN Draft"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <h2 className="font-bold text-navy-900 text-lg">New Inbound ASN</h2>
              <button onClick={() => setModal(false)}><X size={18} className="text-slate-400"/></button>
            </div>
            <div className="overflow-y-auto px-6 py-5 space-y-4 flex-1">
              {error && <p className="text-xs text-red-500">{error}</p>}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Receiving Company *</label>
                  {companies.length > 0 ? (
                    <select value={form.companyId} onChange={e => setForm(f => ({...f, companyId: e.target.value}))}
                      className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                      <option value="">— Select company —</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
                    </select>
                  ) : (
                    <input value={form.companyId} onChange={e => setForm(f => ({...f, companyId: e.target.value}))}
                      placeholder="Company ID (e.g. petgearco)"
                      className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                  )}
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Destination Warehouse</label>
                  <select value={form.warehouseId} onChange={e => setForm(f => ({...f, warehouseId: e.target.value}))}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                    <option value="">— Select warehouse —</option>
                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Expected Arrival Date</label>
                <input type="date" value={form.expectedArrival} onChange={e => setForm(f => ({...f, expectedArrival: e.target.value}))}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Items / Master Data</label>
                  <button onClick={() => setForm(f => ({...f, items: [...f.items, { ...EMPTY_ITEM }]}))}
                    className="text-xs text-violet-600 font-bold flex items-center gap-1 hover:text-violet-800">
                    <Plus size={12}/> Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {form.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <input value={item.sku} onChange={e => setItem(idx, "sku", e.target.value)}
                        placeholder="SKU" className="col-span-2 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                      <input value={item.description} onChange={e => setItem(idx, "description", e.target.value)}
                        placeholder="Description *" className="col-span-3 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                      <input value={item.material} onChange={e => setItem(idx, "material", e.target.value)}
                        placeholder="Material" className="col-span-2 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                      <input type="number" value={item.qty} min="1" onChange={e => setItem(idx, "qty", e.target.value)}
                        placeholder="Qty" className="col-span-2 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                      <input value={item.unit} onChange={e => setItem(idx, "unit", e.target.value)}
                        placeholder="Unit" className="col-span-2 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                      <button onClick={() => setForm(f => ({...f, items: f.items.filter((_,i) => i !== idx)}))}
                        disabled={form.items.length === 1} className="col-span-1 p-2 text-slate-400 hover:text-red-500 disabled:opacity-30">
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5">SKU + Description are used for automatic inventory matching on 3PL validation.</p>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                  rows={2} placeholder="Handling instructions, packaging notes..."
                  className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none"/>
              </div>
            </div>
            <div className="px-6 pb-6 pt-4 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setModal(false)} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreate} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-navy-900 text-white text-sm font-bold hover:bg-navy-800 disabled:opacity-50">
                {saving ? "Creating..." : "Create ASN (Draft)"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
